/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { axiosInstance } from '@coze-arch/bot-http';

export interface PublishedAppItem {
  id: number;
  bot_id: string;
  space_id: string;
  title: string;
  description: string;
  icon_url: string;
  publisher_name: string;
  published_at: number;
  is_publisher: boolean;
}

export interface PublishedAppListResult {
  items: PublishedAppItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface PublishAppResult {
  id: number;
  bot_id: string;
  space_id: string;
  title: string;
  description: string;
  published_at: number;
}

export interface PublishConfigResult {
  found: boolean;
  id?: number;
  scope_type: string;
  scope_dept_ids: string[];
  scope_depts?: Array<{ id: string; name: string }>;
}

interface ApiWrap<T> {
  code: number;
  msg: string;
  data: T;
}

/** 兼容 bot-api 拦截器解包后的 body，或未解包的 AxiosResponse.data */
function unwrapApiData<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') {
    return undefined;
  }
  if ('code' in res && 'data' in res && (res as ApiWrap<T>).code === 0) {
    return (res as ApiWrap<T>).data;
  }
  const axiosBody = (res as { data?: ApiWrap<T> }).data;
  if (axiosBody && axiosBody.code === 0) {
    return axiosBody.data;
  }
  return undefined;
}

export async function getPublishConfig(
  botId: string,
): Promise<PublishConfigResult> {
  const res = await axiosInstance.get('/api/published-apps/publish-config', {
    params: { bot_id: botId },
  });
  const payload = unwrapApiData<PublishConfigResult>(res);
  if (!payload) {
    return { found: false, scope_type: '0', scope_dept_ids: [] };
  }
  return {
    found: payload.found,
    id: payload.id,
    scope_type: payload.scope_type || '0',
    scope_dept_ids: payload.scope_dept_ids ?? [],
    scope_depts: payload.scope_depts ?? [],
  };
}

export async function updatePublishedAppScope(
  id: number,
  params: { scope_type: string; scope_dept_ids?: string[] },
): Promise<void> {
  await axiosInstance.patch(`/api/published-apps/${id}/scope`, params);
}

export async function publishApp(params: {
  bot_id: string;
  space_id: string;
  description?: string;
  scope_type?: string;
  scope_dept_ids?: string[];
}): Promise<PublishAppResult> {
  const res = await axiosInstance.post('/api/published-apps', params);
  const payload = unwrapApiData<PublishAppResult>(res);
  if (!payload) {
    throw new Error('publish app response is empty');
  }
  return payload;
}

export async function listPublishedApps(params?: {
  q?: string;
  page?: number;
  page_size?: number;
}): Promise<PublishedAppListResult> {
  const res = await axiosInstance.get('/api/published-apps', { params });
  const payload = unwrapApiData<PublishedAppListResult>(res);
  if (!payload) {
    return {
      items: [],
      total: 0,
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 50,
    };
  }
  return payload;
}

export async function revokePublishedApp(id: number): Promise<void> {
  await axiosInstance.delete(`/api/published-apps/${id}`);
}
